import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { SelectionBar } from 'components/UI/V3/SelectionBar/SelectionBar';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { NoResults } from '../UI/NoResults/NoResults';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { EmailLocation, EmailSender, SenderStatus } from '../types';
import { formatDate } from '../utils';
import { StatusPill } from '../UI/StatusPill/StatusPill';
import { SENDER_STATUS } from './senderStatus';
import SenderSetupModal from './SenderSetupModal';
import styles from './SenderTab.module.scss';

const STATUS_FILTERS: Array<{ id: SenderStatus | 'all'; name: string }> = [
    { id: 'all', name: 'All' },
    { id: 'verified', name: 'Verified' },
    { id: 'pending', name: 'Pending' },
    { id: 'unconfigured', name: 'Not set up' },
    { id: 'failed', name: 'Failed' }
];

interface Row {
    location: EmailLocation;
    sender: EmailSender;
}

const detailFor = (sender: EmailSender): string => {
    switch (sender.status) {
        case 'verified':
            return `Verified ${formatDate(sender.verifiedAt)}`;
        case 'pending':
            return 'Waiting for the link in the verification email';
        case 'failed':
            return sender.lastError ?? 'Verification failed';
        default:
            return 'Needed to include this location in campaigns';
    }
};

// One sender per location (MarketplaceEmailSender). Only verified locations can be part of a campaign.
export const SenderTab: React.FC = () => {
    const {
        loadingLocations,
        locations,
        scopeIds,
        senders,
        senderFor,
        saveSenderEmail,
        resendVerification,
        refreshSender
    } = useEmailMarketing();
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<SenderStatus | 'all'>('all');
    const [editing, setEditing] = useState<Row | null>(null);

    const scopedRows = useMemo<Array<Row>>(
        () =>
            locations
                .filter(location => scopeIds.includes(location.id))
                // Locations without a sender row yet are shown as not set up.
                .map(location => ({
                    location,
                    sender: senderFor(location.id) ?? {
                        locationId: location.id,
                        status: 'unconfigured' as const,
                        email: null,
                        verifiedAt: null,
                        lastError: null
                    }
                })),
        [locations, scopeIds, senderFor]
    );

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return scopedRows.filter(
            ({ location, sender }) =>
                (status === 'all' || sender.status === status) &&
                (!term || `${location.name} ${location.city} ${sender.email ?? ''}`.toLowerCase().includes(term))
        );
    }, [scopedRows, status, search]);

    // Pending senders may have been confirmed since they were loaded: check them once when the tab opens.
    const pendingIds = senders.filter(sender => sender.status === 'pending').map(sender => sender.locationId);
    useEffect(() => {
        pendingIds.forEach(refreshSender);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingIds.join(',')]);

    const ready = scopedRows.filter(row => row.sender.status === 'verified').length;

    return (
        <div className={styles.root}>
            <div className={styles.banner}>
                <FontAwesomeIcon icon={faCircleInfo} />
                <div>
                    <strong>
                        {ready} of {scopedRows.length} locations ready to send
                    </strong>
                    <p>
                        Each location sends from its own verified email, so customers see the location they know.
                        Locations without a verified sender can&apos;t be included in campaigns.
                    </p>
                </div>
            </div>

            <SearchBar
                searchText={search}
                placeholder="Search by location or email"
                onChangeSearchText={setSearch}
            />

            {/* Same control as the Dashboard tabs and the Campaigns status filter. */}
            <SelectionBar
                options={STATUS_FILTERS}
                selectedId={status}
                background="#f8f8f8"
                className={styles.statusBar}
                onSelect={id => setStatus(id as SenderStatus | 'all')}
            />

            {loadingLocations ? (
                <div className={styles.loading}>
                    <Spinner />
                </div>
            ) : rows.length === 0 ? (
                <NoResults
                    title="No locations found"
                    description={['Try another status, search or location filter.']}
                />
            ) : (
                <Table
                    className={styles.table}
                    firstColumnFixed
                >
                    <TableHeader>
                        <TableHeaderCell>Location</TableHeaderCell>
                        <TableHeaderCell>Sender email</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell>Details</TableHeaderCell>
                        <TableHeaderCell />
                    </TableHeader>
                    <TableBody>
                        {rows.map(row => {
                            const status = SENDER_STATUS[row.sender.status];

                            return (
                                <TableRow key={row.location.id}>
                                    <TableCell className={styles.locationCell}>
                                        <span className={styles.name}>{row.location.name}</span>
                                        <span className={styles.muted}>{row.location.city}</span>
                                    </TableCell>
                                    <TableCell>
                                        {row.sender.email ?? <span className={styles.muted}>Not set up</span>}
                                    </TableCell>
                                    <TableCell>
                                        <StatusPill
                                            label={status.label}
                                            tone={status.tone}
                                        />
                                    </TableCell>
                                    <TableCell className={styles.detailCell}>
                                        <span className={styles.muted}>{detailFor(row.sender)}</span>
                                    </TableCell>
                                    <TableCell isActions>
                                        {row.sender.status === 'pending' && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                label="Resend"
                                                onClick={() => resendVerification(row.location.id)}
                                            />
                                        )}
                                        <Button
                                            size="small"
                                            variant={row.sender.status === 'verified' ? 'outlined' : 'black'}
                                            label={row.sender.email ? 'Change' : 'Set up'}
                                            onClick={() => setEditing(row)}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            {editing && (
                <SenderSetupModal
                    currentEmail={editing.sender.email}
                    locationName={editing.location.name}
                    onClose={() => setEditing(null)}
                    onSave={email => saveSenderEmail(editing.location.id, email)}
                />
            )}
        </div>
    );
};

export default SenderTab;
